import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const timeEntryKeys = {
  byTask: (workspaceSlug: string, taskUlid: string) =>
    ['time-entries', 'task', workspaceSlug, taskUlid] as const,
};

export const taskTimeEntriesQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  taskUlid: string,
) =>
  queryOptions({
    queryKey: timeEntryKeys.byTask(workspaceSlug, taskUlid),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/time-entries',
        {
          params: {
            path: { workspace_slug: workspaceSlug, taskUlid },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useTaskTimeEntries = (
  workspaceSlug: string | undefined,
  taskUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...taskTimeEntriesQueryOptions(api, workspaceSlug ?? '', taskUlid ?? ''),
    enabled: Boolean(workspaceSlug && taskUlid),
  });
};

export interface CreateTimeEntryInput {
  /** ISO 8601. */
  started_at: string;
  /** ISO 8601, omit to start a running timer. */
  ended_at?: string;
  notes?: string;
  billable?: boolean;
}

export const useCreateTimeEntry = (workspaceSlug: string, taskUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTimeEntryInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/time-entries',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: timeEntryKeys.byTask(workspaceSlug, taskUlid) });
      // Starting a timer can auto-stop another running entry on a different
      // task in the same workspace. Bust everything time-entry related so
      // any open task detail in the cache reflects the stop.
      await qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
};

export interface UpdateTimeEntryInput {
  started_at?: string;
  /** Pass `null` to clear (re-running timer); ISO string to set / stop. */
  ended_at?: string | null;
  notes?: string | null;
  billable?: boolean;
}

export const useUpdateTimeEntry = (workspaceSlug: string, entryId: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTimeEntryInput) => {
      // Same nullable-cast pattern as useUpdateTask / useUpdateProject.
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/time-entries/{id}',
        {
          params: { path: { workspace_slug: workspaceSlug, id: entryId } },
          body: input as {
            started_at?: string;
            ended_at?: string;
            notes?: string;
            billable?: boolean;
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
};

export const useDeleteTimeEntry = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await api.DELETE('/v1/workspaces/{workspace_slug}/time-entries/{id}', {
        params: { path: { workspace_slug: workspaceSlug, id: entryId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
};
